import type { UpdateTaskRequest } from "@9amhealth/openapi";
import { TaskResponse } from "@9amhealth/openapi";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import React, { useEffect } from "react";
import type { KnownProgram } from "src/constants/programs";
import type {
  TaskKey,
  TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import {
  DebugButton,
  DebugCheckbox,
  DebugDivider,
  DebugFormGroup,
  DebugInfoBox
} from "./debug.styles";

const DebugTaskMocker: FC = () => {
  const [
    { tasks },
    {
      updateTaskStatus,
      setMockResponseForProgram,
      mockProgramResponsesEnabled,
      setMockProgramResponsesEnabled,
      mockProgramName,
      mockProgramResponses
    }
  ] = useBlocNext(TaskManagementBloc);
  const availableStatus = Object.values(TaskResponse.status);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [mockProgram, setMockProgram] = React.useState<string>(mockProgramName);
  const [mockProgramEnabled, setMockProgramEnabled] = React.useState<boolean>(
    mockProgramResponsesEnabled
  );
  const [mockProgramResponse, setMockProgramResponse] =
    React.useState<string>("");
  const [responseMockValid, setResponseMockValid] =
    React.useState<boolean>(false);

  const handleMockProgramChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMockProgram(e.target.value);
  };

  const handleMockProgramResponseChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setMockProgramResponse(e.target.value);
    try {
      setMockResponseForProgram(mockProgram, e.target.value);
      setResponseMockValid(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error parsing mock response:", error);
      setResponseMockValid(false);
    }
  };

  useEffect(() => {
    if (mockProgram && mockProgramResponses[mockProgram]) {
      try {
        setMockProgramResponse(
          JSON.stringify(mockProgramResponses[mockProgram], null, 2)
        );
        setResponseMockValid(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error parsing mock response:", error);
        setResponseMockValid(false);
      }
    }
  }, [mockProgram, mockProgramResponses]);

  const handleMockProgramEnabledChange = (enabled: boolean) => {
    setMockProgramEnabled(enabled);
    setMockProgramResponsesEnabled(enabled);
  };

  const programs = new Set<KnownProgram>();
  const groups = new Set<string>();
  const slugs = new Set<string>();

  tasks.forEach((task) => {
    programs.add(task.program as KnownProgram);
    groups.add(task.group);
    slugs.add(task.slug);
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const values = new FormData(e.currentTarget);

    const t = {
      program: values.get("program") as KnownProgram,
      group: values.get("group") as string,
      slug: values.get("slug") as TaskKey
    };

    const status = values.get("status") as UpdateTaskRequest.status;
    setError("");
    setLoading(true);
    void updateTaskStatus(t as TaskResponseKnown, status)
      .catch((tmError: unknown) => {
        setError(String((tmError as { message?: string }).message));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div>
      <h4 style={{ fontSize: "13px", margin: "0 0 0.75rem", fontWeight: 600 }}>
        Change Task Status
      </h4>
      <form onSubmit={handleSubmit}>
        <DebugFormGroup>
          <label>Program</label>
          <input type="text" name="program" list="programs" />
          <datalist id="programs">
            {Array.from(programs).map((program) => (
              <option key={program} value={program} />
            ))}
          </datalist>
        </DebugFormGroup>

        <DebugFormGroup>
          <label>Group</label>
          <input type="text" name="group" list="groups" />
          <datalist id="groups">
            {Array.from(groups).map((group) => (
              <option key={group} value={group} />
            ))}
          </datalist>
        </DebugFormGroup>

        <DebugFormGroup>
          <label>Slug</label>
          <input type="text" name="slug" list="slugs" />
          <datalist id="slugs">
            {Array.from(slugs).map((slug) => (
              <option key={slug} value={slug} />
            ))}
          </datalist>
        </DebugFormGroup>

        <DebugFormGroup>
          <label>Status</label>
          <select name="status">
            {availableStatus.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </DebugFormGroup>

        <DebugButton type="submit" disabled={loading}>
          {loading ? "Submitting..." : "Update Task"}
        </DebugButton>

        {error && (
          <DebugInfoBox variant="warning" style={{ marginTop: "0.75rem" }}>
            {error}
          </DebugInfoBox>
        )}
      </form>

      <DebugDivider />

      <h4 style={{ fontSize: "13px", margin: "0 0 0.75rem", fontWeight: 600 }}>
        Mock API Response
      </h4>

      <DebugCheckbox style={{ marginBottom: "0.75rem" }}>
        <input
          type="checkbox"
          name="mockTaskResponse"
          checked={mockProgramEnabled}
          onChange={(e) => handleMockProgramEnabledChange(e.target.checked)}
        />
        <span>Enable Mock Responses</span>
      </DebugCheckbox>

      <DebugFormGroup>
        <label>Program</label>
        <input
          name="mockProgram"
          type="text"
          value={mockProgram}
          onInput={handleMockProgramChange}
          list="programs"
        />
      </DebugFormGroup>

      <DebugFormGroup>
        <label>Response (JSON)</label>
        <textarea
          name="mockProgramResponse"
          rows={6}
          value={mockProgramResponse}
          onInput={handleMockProgramResponseChange}
          style={{
            borderColor:
              mockProgramResponse && !responseMockValid ? "#dc2626" : undefined
          }}
        />
      </DebugFormGroup>

      {mockProgramResponse && !responseMockValid && (
        <DebugInfoBox variant="warning">Invalid JSON format</DebugInfoBox>
      )}
    </div>
  );
};

export default DebugTaskMocker;
